import Header, { HeaderProps } from "./Header";
import Body, { BodyProps } from "./Body";
import Footer, { FooterProps } from "./Footer";
import { cn } from "@/lib/utils";
import { Action, AdStatus } from "./types";
import { AdCreative } from "@/screens/creative-library/types";
import { getPlatformIcon } from "@/screens/utils/Helpers";
import { Platform } from "@/screens/creatives/types";
import { useEffect, useState } from "react";
import { fetchCreative, fetchMedia } from "@/screens/creative-library/apiService";
import { useParams } from "react-router-dom";

interface AdCardProps {
  header?: HeaderProps;
  body: BodyProps;
  footer?: FooterProps;
  className?: string;
  status?: AdStatus;
  processingText?: boolean;
  processingMsg?: string;
  winningCriteria?: { criteriaName: string; colorCode: string }[];
}

const AdCard = ({
  header,
  body,
  footer,
  className,
  status = "success",
  processingText,
  processingMsg,
  winningCriteria,
}: AdCardProps) => {
  if (status && status === "failed") return <></>;
  return (
    <div
      className={cn(
        "group w-full flex flex-col justify-start items-start self-start rounded-[12px] border-[1px] border-border-gray bg-white shadow-sm overflow-visible",
        header && "p-2",
        className,
      )}
    >
      {header && <Header {...header} />}
      <Body
        {...body}
        status={status}
        processingText={processingText}
        processingMsg={processingMsg}
        winningCriteria={winningCriteria || body.winningCriteria}
      />
      {footer && <Footer {...footer} />}
    </div>
  );
};

interface PollingAdCardProps {
  className?: string;
  adCreative: AdCreative;
  accountIconUrl?: string;
  mediaActions: Action[];
  onMediaClick?: () => void;
  onPollingComplete?: (creative: AdCreative) => void;
  currentImageIndex?: number;
  calculateHeight?: boolean;
  onCreativeUpdate?: (updated: AdCreative) => void;
  ctaCopyEnabled?: boolean;
  enablePolling?: boolean;
}
export const PollingAdCard = ({
  className = "",
  adCreative,
  mediaActions,
  onMediaClick,
  onPollingComplete,
  accountIconUrl,
  currentImageIndex,
  calculateHeight = true,
  onCreativeUpdate,
  ctaCopyEnabled,
  enablePolling = true,
}: PollingAdCardProps) => {
  const { accountId } = useParams();
  const [polledAdCreative, setPolledAdCreative] = useState<AdCreative>(adCreative);
  // Use the polled creative for rendering so UI updates automatically
  const creativeToRender = polledAdCreative ?? adCreative;
  // Get available images for switching
  const availableImages = creativeToRender.creative?.images || [];
  // Get current image based on index
  const currentImage =
    currentImageIndex !== undefined
      ? availableImages[currentImageIndex]
      : availableImages[0];

  const status = currentImage ? currentImage.status : creativeToRender.status;

  useEffect(() => {
    setPolledAdCreative(adCreative);
  }, [adCreative]);

  useEffect(() => {
    if (accountId && adCreative.status === "success") {
      setPolledAdCreative(adCreative);
      return;
    }
    if (!enablePolling) return;
    if (!accountId || adCreative.status !== "pending") return;
    // Don't poll optimistic creatives (they will be replaced with real data from API)
    if (adCreative.id.startsWith("optimistic-")) return;

    const abortController = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout>;

    const pollCreative = async () => {
      try {
        const res = await fetchCreative(accountId, adCreative.id, {
          signal: abortController.signal,
        });
        const updatedCreative = res.data;
        onCreativeUpdate?.(updatedCreative);
        setPolledAdCreative(updatedCreative);

        if (updatedCreative.status === "pending")
          timeoutId = setTimeout(() => void pollCreative(), 5000); // poll every 5 seconds
        else onPollingComplete?.(updatedCreative);
      } catch (error) {
        if (error instanceof Error && error.name !== "AbortError") {
          console.error("Error while polling creative:", error);
        }
      }
    };

    void pollCreative(); // Initial poll

    return () => {
      abortController.abort();
      if (timeoutId) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enablePolling,
    accountId,
    adCreative.id,
    adCreative.status,
    adCreative.creative?.images?.length,
  ]);

  // Poll current image if it's pending
  useEffect(() => {
    if (!enablePolling) return;
    if (!accountId || !currentImage || currentImage.status !== "pending") return;
    // Don't poll optimistic images (they will be replaced with real data from API)
    if (currentImage.id.startsWith("optimistic-")) return;

    const abortController = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout>;

    const pollCurrentImage = async () => {
      try {
        const res = await fetchMedia(accountId, currentImage.id, {
          signal: abortController.signal,
        });
        const updatedImage = res.data;
        // Update the creative with the new image data
        setPolledAdCreative((prev) => {
          if (!prev) return prev;
          const updatedImages =
            prev.creative.images?.map((img) =>
              img.id === currentImage.id ? updatedImage : img,
            ) || [];
          const updatedCreative = {
            ...prev,
            creative: {
              ...prev.creative,
              images: updatedImages,
            },
          };
          onCreativeUpdate?.(updatedCreative);

          return updatedCreative;
        });

        if (updatedImage.status === "pending") {
          timeoutId = setTimeout(() => void pollCurrentImage(), 2000); // poll every 5 seconds
        }
      } catch (error) {
        if (error instanceof Error && error.name !== "AbortError") {
          console.error("Error while polling current image:", error);
        }
      }
    };
    void pollCurrentImage(); // Initial poll

    return () => {
      abortController.abort();
      if (timeoutId) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enablePolling, accountId, currentImage?.id, currentImage?.status]);

  return (
    <AdCard
      className={className}
      header={
        ctaCopyEnabled
          ? {
              icon: creativeToRender?.creative?.account_icon_url || accountIconUrl,
              title: creativeToRender?.creative?.account_name ?? "",
            }
          : undefined
      }
      body={{
        icon_src: ctaCopyEnabled
          ? getPlatformIcon(creativeToRender.creative.platform ?? Platform.Facebook)
          : undefined,
        name: ctaCopyEnabled ? creativeToRender.name : undefined,
        description: ctaCopyEnabled ? creativeToRender?.creative.bodies?.[0] : undefined,
        media: {
          ad_id: creativeToRender.id || "",
          source: creativeToRender.creative?.videos?.[0]
            ? creativeToRender.creative?.videos?.[0]?.permalink_url
            : currentImage?.permalink_url,
          type: creativeToRender.creative?.videos?.[0] ? "video" : "image",
          thumbnail:
            creativeToRender.creative?.thumbnail_url ||
            creativeToRender.creative?.videos?.[0]?.thumbnail_url,
          onClick: creativeToRender.status === "success" ? onMediaClick : undefined,
          actions: mediaActions,
          original_source:
            creativeToRender?.creative?.text_processing?.metadata?.original_image_url,
          crossOrigin: "anonymous",
          aspectRatio: calculateHeight ? currentImage?.aspect_ratio : undefined,
        },
      }}
      footer={
        ctaCopyEnabled
          ? {
              cta_label: creativeToRender.creative.call_to_action_titles?.[0] ?? "",
              landing_page: creativeToRender.creative.landing_pages?.[0] ?? "",
              title: creativeToRender.creative.titles?.[0] ?? "",
            }
          : undefined
      }
      status={status}
      processingText={
        creativeToRender?.creative?.text_processing &&
        creativeToRender.status === "pending"
      }
      processingMsg={creativeToRender?.progress ?? ""}
    />
  );
};
export default AdCard;
